import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type ThemeMode = 'light' | 'dark' | 'auto'

export interface ThemeSchedule {
  lightStartHour: number // 0-23, when light mode begins
  lightStartMinute: number
  lightEndHour: number // 0-23, when dark mode begins
  lightEndMinute: number
  timezone: string // IANA timezone e.g. 'America/New_York'
}

export interface DashboardThemeOverride {
  dashboardId: string
  mode: ThemeMode | 'global' // 'global' = follow global setting
  schedule?: ThemeSchedule
}

interface ThemeState {
  // Global settings
  mode: ThemeMode
  schedule: ThemeSchedule
  resolvedTheme: 'light' | 'dark' // the actual applied theme

  // Per-dashboard overrides
  dashboardOverrides: Record<string, DashboardThemeOverride>

  // Current dashboard context
  activeDashboard: string | null

  // Actions
  setMode: (mode: ThemeMode) => void
  setSchedule: (schedule: Partial<ThemeSchedule>) => void
  setDashboardOverride: (dashboardId: string, override: Partial<DashboardThemeOverride>) => void
  removeDashboardOverride: (dashboardId: string) => void
  setActiveDashboard: (dashboardId: string | null) => void
  setResolvedTheme: (theme: 'light' | 'dark') => void
}

const DEFAULT_SCHEDULE: ThemeSchedule = {
  lightStartHour: 7,
  lightStartMinute: 0,
  lightEndHour: 19, // 7 PM
  lightEndMinute: 0,
  timezone: 'America/New_York',
}

export function isWithinLightSchedule(schedule: ThemeSchedule): boolean {
  const now = new Date()

  // Get current time in the configured timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: schedule.timezone,
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  })
  const parts = formatter.formatToParts(now)
  const hour = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0')
  const minute = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0')

  const currentMinutes = hour * 60 + minute
  const startMinutes = schedule.lightStartHour * 60 + schedule.lightStartMinute
  const endMinutes = schedule.lightEndHour * 60 + schedule.lightEndMinute

  if (startMinutes <= endMinutes) {
    return currentMinutes >= startMinutes && currentMinutes < endMinutes
  }
  // Wraps around midnight
  return currentMinutes >= startMinutes || currentMinutes < endMinutes
}

export function resolveTheme(
  mode: ThemeMode,
  schedule: ThemeSchedule,
  activeDashboard: string | null,
  overrides: Record<string, DashboardThemeOverride>
): 'light' | 'dark' {
  // Check dashboard override first
  if (activeDashboard && overrides[activeDashboard]) {
    const override = overrides[activeDashboard]
    if (override.mode !== 'global') {
      if (override.mode === 'auto') {
        const sched = override.schedule ?? schedule
        return isWithinLightSchedule(sched) ? 'light' : 'dark'
      }
      return override.mode
    }
  }

  // Fall back to global
  if (mode === 'auto') {
    return isWithinLightSchedule(schedule) ? 'light' : 'dark'
  }
  return mode
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'auto',
      schedule: DEFAULT_SCHEDULE,
      resolvedTheme: 'light',
      dashboardOverrides: {},
      activeDashboard: null,

      setMode: (mode) => {
        set({ mode })
        const state = get()
        set({
          resolvedTheme: resolveTheme(mode, state.schedule, state.activeDashboard, state.dashboardOverrides),
        })
      },

      setSchedule: (partial) => {
        const schedule = { ...get().schedule, ...partial }
        set({ schedule })
        const state = get()
        set({
          resolvedTheme: resolveTheme(state.mode, schedule, state.activeDashboard, state.dashboardOverrides),
        })
      },

      setDashboardOverride: (dashboardId, override) => {
        const overrides = { ...get().dashboardOverrides }
        overrides[dashboardId] = {
          ...overrides[dashboardId],
          dashboardId,
          mode: 'global',
          ...override,
        }
        set({ dashboardOverrides: overrides })
        const state = get()
        set({
          resolvedTheme: resolveTheme(state.mode, state.schedule, state.activeDashboard, overrides),
        })
      },

      removeDashboardOverride: (dashboardId) => {
        const overrides = { ...get().dashboardOverrides }
        delete overrides[dashboardId]
        set({ dashboardOverrides: overrides })
        const state = get()
        set({
          resolvedTheme: resolveTheme(state.mode, state.schedule, state.activeDashboard, overrides),
        })
      },

      setActiveDashboard: (dashboardId) => {
        set({ activeDashboard: dashboardId })
        const state = get()
        set({
          resolvedTheme: resolveTheme(state.mode, state.schedule, dashboardId, state.dashboardOverrides),
        })
      },

      setResolvedTheme: (theme) => set({ resolvedTheme: theme }),
    }),
    {
      name: 'cornerstone-theme',
      partialize: (state) => ({
        mode: state.mode,
        schedule: state.schedule,
        dashboardOverrides: state.dashboardOverrides,
      }),
    }
  )
)
